import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Eye, Download, LogOut, Phone, Mail, Home, Users, MapPin, RefreshCw, Search, AlertTriangle, BarChart3, UserPlus, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = `${(typeof window !== 'undefined' && window.location?.origin) || process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = ({ token, onLogout }) => {
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [conversion, setConversion] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [comLoading, setComLoading] = useState(true);

  const fetchCommerciaux = useCallback(async () => {
    try {
      setComLoading(true);
      const { data } = await axios.get(`${API}/commerciaux`, { headers: { Authorization: `Bearer ${token}` } });
      setCommerciaux(Array.isArray(data) ? data : []);
    } catch (e) { /* silencieux */ }
    finally { setComLoading(false); }
  }, [token]);

  const commercialLink = (code) => `${window.location.origin}/?c=${code}`;
  const copyLink = async (code) => {
    try { await navigator.clipboard.writeText(commercialLink(code)); toast.success('Lien copié ✓'); }
    catch (e) { toast.error('Copie impossible'); }
  };

  const handleRetryCrm = async (id) => {
    try {
      setRetryingId(id);
      const { data } = await axios.post(`${API}/estimations/${id}/retry-crm`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.status === 'sent') toast.success('Envoi au CRM réussi ✓');
      else toast.error("L'envoi au CRM a de nouveau échoué");
      await fetchEstimations();
      setSelected((prev) => (prev ? { ...prev, crm_status: data.status } : prev));
    } catch (e) {
      toast.error("Erreur lors du renvoi au CRM");
    } finally {
      setRetryingId(null);
    }
  };

  const fetchEstimations = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/estimations`, { headers: { Authorization: `Bearer ${token}` } });
      setEstimations(data);
      try {
        const conv = await axios.get(`${API}/analytics/conversion`, { headers: { Authorization: `Bearer ${token}` } });
        setConversion(conv.data);
      } catch (e) { /* analytics facultatif */ }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        onLogout();
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchEstimations(); }, [fetchEstimations]);
  useEffect(() => { fetchCommerciaux(); }, [fetchCommerciaux]);

  const formatDate = (s) => {
    if (!s) return '-';
    try {
      return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return s; }
  };

  const villes = (e) => [e.ville_choix_1, e.ville_choix_2, e.ville_choix_3].filter(Boolean).join(', ');
  const num = (v) => (v ? Number(v).toLocaleString('fr-FR') : '0');

  const crmBadge = (e) => {
    const s = e.crm_status;
    if (s === 'sent') return <Badge className="bg-green-100 text-green-700 border border-green-200" data-testid="crm-status-sent">CRM ✓</Badge>;
    if (s === 'failed') return <Badge className="bg-[#F3EAD8] text-[#8F7344] border border-[#DFCE9F]" data-testid="crm-status-failed">CRM ✗</Badge>;
    if (s === 'rejected') return <Badge className="bg-orange-100 text-orange-700 border border-orange-200" data-testid="crm-status-rejected">CRM ⚠ Rejeté</Badge>;
    if (s === 'pending') return <Badge className="bg-amber-100 text-amber-700 border border-amber-200" data-testid="crm-status-pending">CRM … en cours</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 border border-amber-200" data-testid="crm-status-unknown">CRM … à relancer</Badge>;
  };
  const crmFailed = estimations.filter((e) => e.crm_status === 'failed').length;
  const channelLabel = (c) => ({ direct: 'Direct', whatsapp: 'WhatsApp', meta: 'Meta', google: 'Google', email: 'Email' }[c] || c);

  const exportCSV = () => {
    const headers = ['Date', 'Nom', 'Prénom', 'Téléphone', 'Email', 'Type bien', 'Pièces', 'Villes', 'Apport', 'Critères', 'Durée trajet', 'Station'];
    const rows = estimations.map((e) => [
      formatDate(e.timestamp), e.nom, e.prenom, e.telephone, e.email,
      e.type_bien, e.nombre_chambres, villes(e), e.apport_projet, e.criteres, e.duree_trajet, e.station_emploi,
    ].map((x) => `"${(x ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const today = estimations.filter((e) => e.timestamp && new Date(e.timestamp).toDateString() === new Date().toDateString()).length;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? estimations.filter((e) => [e.prenom, e.nom, e.telephone, e.email, villes(e), e.type_bien, formatDate(e.timestamp)]
        .some((f) => (f ?? '').toString().toLowerCase().includes(q)))
    : estimations;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B09259] mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement des soumissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Soumissions reçues</h1>
            <p className="text-slate-600">LOVELIA — sauvegarde locale des prospects</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={fetchEstimations} variant="outline" data-testid="admin-refresh-button">
              <RefreshCw className="h-4 w-4 mr-2" />Actualiser
            </Button>
            <Button onClick={exportCSV} className="bg-green-600 hover:bg-green-700" data-testid="admin-export-button">
              <Download className="h-4 w-4 mr-2" />Exporter CSV
            </Button>
            <Button onClick={onLogout} variant="outline" className="text-[#8F7344] border-[#DFCE9F]" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-2" />Déconnexion
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="p-6 flex items-center"><Users className="h-8 w-8 text-[#8F7344]" /><div className="ml-4"><p className="text-sm text-slate-600">Total prospects</p><p className="text-2xl font-bold">{estimations.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-6 flex items-center"><Home className="h-8 w-8 text-slate-600" /><div className="ml-4"><p className="text-sm text-slate-600">Aujourd'hui</p><p className="text-2xl font-bold">{today}</p></div></CardContent></Card>
          <Card><CardContent className="p-6 flex items-center"><MapPin className="h-8 w-8 text-amber-600" /><div className="ml-4"><p className="text-sm text-slate-600">Apport moyen</p><p className="text-2xl font-bold">{estimations.length ? num(Math.round(estimations.reduce((s, e) => s + (Number(e.apport_projet) || 0), 0) / estimations.length)) : 0}€</p></div></CardContent></Card>
          <Card data-testid="crm-failed-stat"><CardContent className="p-6 flex items-center"><AlertTriangle className={`h-8 w-8 ${crmFailed ? 'text-[#8F7344]' : 'text-green-600'}`} /><div className="ml-4"><p className="text-sm text-slate-600">Échecs CRM</p><p className={`text-2xl font-bold ${crmFailed ? 'text-[#8F7344]' : ''}`}>{crmFailed}</p></div></CardContent></Card>
        </div>

        {/* Taux de conversion par canal */}
        {conversion && conversion.rows && conversion.rows.length > 0 && (
          <Card className="mb-8" data-testid="conversion-analytics">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-slate-800 flex items-center"><BarChart3 className="h-5 w-5 mr-2 text-[#8F7344]" />Taux de conversion par canal</CardTitle>
              <CardDescription>Ouvertures de lien vs questionnaires complétés (cumul à partir de la mise en service)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-4 font-medium">Canal</th>
                      <th className="py-2 pr-4 font-medium text-right">Ouvertures</th>
                      <th className="py-2 pr-4 font-medium text-right">Complétés</th>
                      <th className="py-2 font-medium text-right">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversion.rows.map((r) => (
                      <tr key={r.channel} className="border-b border-slate-50" data-testid={`conversion-row-${r.channel}`}>
                        <td className="py-2 pr-4"><Badge variant="outline" className="text-slate-700">{channelLabel(r.channel)}</Badge></td>
                        <td className="py-2 pr-4 text-right">{r.opens}</td>
                        <td className="py-2 pr-4 text-right font-medium">{r.completed}</td>
                        <td className="py-2 text-right">{r.rate === null ? '—' : <span className={`font-semibold ${r.rate >= 30 ? 'text-green-600' : r.rate >= 10 ? 'text-amber-600' : 'text-slate-600'}`}>{r.rate}%</span>}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold text-slate-800">
                      <td className="py-2 pr-4">Total</td>
                      <td className="py-2 pr-4 text-right">{conversion.totals.opens}</td>
                      <td className="py-2 pr-4 text-right">{conversion.totals.completed}</td>
                      <td className="py-2 text-right">{conversion.totals.rate === null ? '—' : `${conversion.totals.rate}%`}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-8" data-testid="commerciaux-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-slate-800 flex items-center"><UserPlus className="h-5 w-5 mr-2 text-[#8F7344]" />Commerciaux</CardTitle>
                <CardDescription>Liste synchronisée automatiquement depuis le CRM (lecture seule). Ajoutez/modifiez vos commerciaux directement dans le CRM — cette liste se met à jour toute seule.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={fetchCommerciaux} data-testid="commerciaux-refresh-button"><RefreshCw className="h-3 w-3 mr-1" />Rafraîchir</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {comLoading && commerciaux.length === 0 && <p className="text-slate-500 text-sm">Chargement depuis le CRM…</p>}
              {!comLoading && commerciaux.length === 0 && <p className="text-slate-500 text-sm">Aucun commercial trouvé dans le CRM.</p>}
              {commerciaux.map((c) => (
                <div key={c.code} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-slate-100 rounded-lg p-3" data-testid="commercial-row">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 flex items-center gap-2">
                      {c.name}
                      {c.is_default && <span className="text-[10px] uppercase tracking-wide bg-[#FAF6EE] text-[#8F7344] border border-[#DFCE9F] rounded px-1.5 py-0.5">Par défaut</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{c.email}{c.whatsapp ? ` · WhatsApp ${c.whatsapp}` : ''}{c.booking_url ? ' · RDV en ligne' : ''}</p>
                    <p className="text-xs text-slate-600 truncate mt-1"><span className="font-mono bg-slate-50 px-1 py-0.5 rounded">{commercialLink(c.code)}</span></p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copyLink(c.code)} data-testid="commercial-copy-button"><Copy className="h-3 w-3 mr-1" />Copier le lien</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-slate-800">Liste ({filtered.length}{q ? ` / ${estimations.length}` : ''})</h2>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher (nom, ville, tél, email, date)"
                  className="pl-9"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {filtered.length === 0 && <p className="text-slate-500">{q ? 'Aucun résultat pour cette recherche.' : 'Aucune soumission enregistrée.'}</p>}
              {filtered.map((e) => (
                <Card key={e.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(e)} data-testid="admin-prospect-card">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-slate-800">{e.prenom} {e.nom}</h3>
                        <p className="text-sm text-slate-600">{formatDate(e.timestamp)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {crmBadge(e)}
                        <Badge variant="outline" className="text-[#8F7344]">{e.type_bien || '—'}</Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                      <span className="flex items-center"><Phone className="h-3 w-3 mr-1" />{e.telephone || '—'}</span>
                      <span className="flex items-center"><MapPin className="h-3 w-3 mr-1" />{villes(e) || '—'}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            {selected ? (
              <Card data-testid="admin-prospect-detail">
                <CardHeader>
                  <CardTitle className="text-[#6E5836]">{selected.prenom} {selected.nom}</CardTitle>
                  <CardDescription>Reçu le {formatDate(selected.timestamp)}</CardDescription>
                  <div className="pt-1 flex items-center gap-2 flex-wrap">
                    {crmBadge(selected)}
                    {(selected.crm_status === 'failed' || selected.crm_status === 'rejected') && selected.crm_error && <span className="text-xs text-slate-500">{selected.crm_error}</span>}
                    {(selected.crm_status === 'failed' || selected.crm_status === 'rejected' || selected.crm_status === 'pending' || !selected.crm_status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#DFCE9F] text-[#8F7344] hover:bg-[#FAF6EE] h-7"
                        onClick={() => handleRetryCrm(selected.id)}
                        disabled={retryingId === selected.id}
                        data-testid="retry-crm-button"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${retryingId === selected.id ? 'animate-spin' : ''}`} />
                        {retryingId === selected.id ? 'Envoi…' : "Retenter l'envoi CRM"}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-1">Contact</h4>
                    <p><Phone className="h-3 w-3 inline mr-2" />{selected.telephone}</p>
                    <p><Mail className="h-3 w-3 inline mr-2" />{selected.email}</p>
                    <p><Home className="h-3 w-3 inline mr-2" />{selected.adresse} {selected.code_postal} {selected.ville}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-1">Situation</h4>
                    <p>Situation familiale : {selected.situation_familiale || '—'}</p>
                    <p>Enfants : {selected.nombre_enfants || 0}</p>
                    <p>Apport : {num(selected.apport_projet)}€ — Mensualité souhaitée : {num(selected.mensualite_souhaitee)}€</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-1">Projet</h4>
                    <p>Bien : {selected.type_bien || '—'} — {selected.nombre_chambres || '—'} pièce(s)</p>
                    <p>Villes souhaitées : {villes(selected) || '—'}</p>
                    <p>Critères : {selected.criteres || '—'}</p>
                    <p>Durée max trajet : {selected.duree_trajet || '—'} {selected.station_emploi ? `(${selected.station_emploi})` : ''}</p>
                    {selected.description_bien && <p className="bg-slate-50 p-2 rounded mt-1">{selected.description_bien}</p>}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-96 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Sélectionnez un prospect pour voir le détail</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
